import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { translate } from '@jsverse/transloco';
import { EMPTY, Observable, forkJoin } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { InventoryService } from 'src/app/inventory/services/inventory.service';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { IAttachmentResponse } from 'src/app/shared/services/attachment.service';

export interface InventoryAttachmentState {
  attachments: IAttachmentResponse[];
  isLoading: boolean;
}

/**
 * Attachments (documents and pictures) of an inventory device.
 * Kept separate from the generic AttachmentStore, which is coupled to the device stores.
 */
@Injectable()
export class InventoryAttachmentStore extends ComponentStore<InventoryAttachmentState> {

  constructor(
    private inventoryService: InventoryService,
    private appnotificationService: AppNotificationService) {
      super({
        attachments: [],
        isLoading: false
      });
  }

  readonly attachments$: Observable<IAttachmentResponse[]> = this.select(state => state.attachments);

  readonly images$: Observable<IAttachmentResponse[]> = this.select(
    this.attachments$,
    attachments => attachments.filter(attachment => attachment.fileExtension?.startsWith('image/'))
  );

  readonly documents$: Observable<IAttachmentResponse[]> = this.select(
    this.attachments$,
    attachments => attachments.filter(attachment => attachment.fileExtension?.startsWith('application/pdf'))
  );

  readonly isLoading$: Observable<boolean> = this.select(state => state.isLoading);

  // Effect to load the attachments of an inventory device
  readonly getAttachmentsByInventoryDevice = this.effect<string>(inventoryDeviceId$ =>
    inventoryDeviceId$.pipe(
      tap(() => this.patchState({ isLoading: true })),
      switchMap(inventoryDeviceId =>
        this.inventoryService.getAttachmentsByInventoryDevice({ inventoryDeviceId }).pipe(
          tap(response => this.patchState({ attachments: response.data, isLoading: false })),
          catchError(() => {
            this.patchState({ isLoading: false });
            return EMPTY;
          })
        )
      )
    )
  );

  // Effect to upload multiple files
  readonly addAttachments = this.effect<FormData[]>(attachments$ =>
    attachments$.pipe(
      tap(() => this.patchState({ isLoading: true })),
      switchMap(attachments =>
        forkJoin(attachments.map(attachment => this.inventoryService.uploadAttachment(attachment))).pipe(
          tap(newAttachments => {
            this.patchState(state => ({
              attachments: [...state.attachments, ...newAttachments],
              isLoading: false
            }));
            this.appnotificationService.notification$.next({ message: translate('attachment.createSuccess'), style: 'success' });
          }),
          catchError(() => {
            this.patchState({ isLoading: false });
            return EMPTY;
          })
        )
      )
    )
  );

  // Effect to delete a file
  readonly removeAttachmentById = this.effect<string>(attachmentId$ =>
    attachmentId$.pipe(
      switchMap(attachmentId =>
        this.inventoryService.deleteAttachment(attachmentId).pipe(
          tap(() => {
            this.patchState(state => ({
              attachments: state.attachments.filter(attachment => attachment.id !== attachmentId)
            }));
            this.appnotificationService.notification$.next({ message: translate('attachment.deleteSuccess'), style: 'success' });
          }),
          catchError(() => {
            this.appnotificationService.notification$.next({ message: translate('attachment.deleteFailed'), style: 'error' });
            return EMPTY;
          })
        )
      )
    )
  );
}